import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { SearchbarComponent } from '../header/searchbar/searchbar.component';
import { TranslatePipe } from '@ngx-translate/core';
import { ExternalPortalsComponent } from '../external-portals/external-portals.component';
import { UserService } from '../../../common/service/user.service';
import { PlatformService } from '../../../common/service/platform.service';
import { MaintenanceBannerComponent } from "../maintenance-banner/maintenance-banner.component";
import { ButtonModule } from 'primeng/button';
import { UserPreferenceService } from '../../../common/service/user-preference.service';

@Component({
  selector: 'gate-hero',
  standalone: true,
  imports: [SearchbarComponent, TranslatePipe, ExternalPortalsComponent, MaintenanceBannerComponent, ButtonModule],
  templateUrl: './hero.component.html',
  styles: [`
    .hero-shell {
      height: 512px;
      opacity: 1;
      overflow: hidden;
      position: relative;
      transition:
        height 1400ms cubic-bezier(0.22, 1, 0.36, 1),
        opacity 1400ms cubic-bezier(0.22, 1, 0.36, 1);
      will-change: height, opacity;
    }

    .hero-shell-closed {
      height: 0;
      opacity: 0;
      pointer-events: none;
    }

    .hero-content {
      opacity: 1;
      transform: translateY(0);
      transition:
        opacity 520ms cubic-bezier(0.22, 1, 0.36, 1),
        transform 820ms cubic-bezier(0.22, 1, 0.36, 1);
      will-change: opacity, transform;
    }

    .hero-content-hidden {
      opacity: 0;
      transform: translateY(-1.25rem);
    }

    .hero-close-button {
      background: var(--p-surface-200);
      border: var(--p-surface-300);
      color: #313B42;
    }
      .hero-close-button:hover {
        background: var(--p-surface-300) !important;
        border: var(--p-surface-400) !important;
        color: #313B42 !important;
      }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeroComponent {
  heroVisible = signal(true);
  maintenanceBannerVisible = signal(false);
  private readonly userService = inject(UserService);
  private readonly platformService = inject(PlatformService);
  private readonly userPreferenceService = inject(UserPreferenceService);
  user = computed(() => this.userService.currentUser());
  isImpersonatingAsAcpUser = computed(() => this.userService.isImpersonating() && this.userService.getImpersonationContext()?.initiatorType === 'ACP_User');
  constructor() {
  // Set the initial hero section visibility from the stored user preference.
this.heroVisible.set(this.userPreferenceService.showHeroSection());

// Initialize user preferences (e.g., load them from storage or the backend).
// Once loaded, update the hero visibility and notify other components.
this.userPreferenceService.initialize().then(() => {
  this.heroVisible.set(this.userPreferenceService.showHeroSection());
  this.platformService.heroToggleEvent.emit(this.heroVisible());
});

// Emit the current hero visibility so components have an initial state
// before any toggle events occur.
this.platformService.heroToggleEvent.emit(this.heroVisible());

// Listen for hero visibility changes triggered from other components
// and update the local state accordingly.
this.platformService.heroToggleEvent.subscribe((value) => {
  this.setHeroVisible(value, false);
});
  }

  toggleHeroVisible(nextValue?: boolean) {
    const value = nextValue ?? !this.heroVisible();
    this.setHeroVisible(value, false);
    this.platformService.heroToggleEvent.emit(this.heroVisible());
    this.userPreferenceService.setShowHeroSection(value);
  }

  onMaintenanceBannerVisibilityChange(isVisible: boolean) {
    this.maintenanceBannerVisible.set(isVisible);
  }

  private setHeroVisible(value: boolean, persist = true) {
    this.heroVisible.set(value);
    if (persist) {
      this.userPreferenceService.setShowHeroSection(value);
    }
  }
}
